import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SiteNav } from "./site/SiteChrome";
import {
  ArrowRight,
  Home,
  Brain,
  Users,
  Network,
  GitBranch,
  Sparkles,
  History,
  Boxes,
  Settings,
  Shield,
  Activity,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  Compass,
  Stethoscope,
  HeartPulse,
  Wallet,
} from "lucide-react";

/* ============================================================
   Animated network background (Palantir-esque)
   ============================================================ */
const NetworkBg = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let raf;
    let nodes = [];
    let mouse = { x: -999, y: -999 };
    const setup = () => {
      c.width = window.innerWidth;
      c.height = window.innerHeight;
      const count = window.innerWidth < 768 ? 30 : 80;
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * c.width,
        y: Math.random() * c.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.4 + 0.6,
        color: Math.random() > 0.75 ? "201,168,76" : "0,229,204",
      }));
    };
    setup();
    const onResize = () => setup();
    const onMove = (e) => { mouse.x = e.clientX; mouse.y = e.clientY; };
    window.addEventListener("resize", onResize);
    window.addEventListener("mousemove", onMove);

    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      nodes.forEach((n) => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > c.width) n.vx *= -1;
        if (n.y < 0 || n.y > c.height) n.vy *= -1;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${n.color},0.5)`;
        ctx.fill();
      });
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const d = Math.hypot(dx, dy);
          if (d < 150) {
            ctx.strokeStyle = `rgba(0,229,204,${0.08 * (1 - d / 150)})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
        // Mouse attraction line
        const md = Math.hypot(nodes[i].x - mouse.x, nodes[i].y - mouse.y);
        if (md < 200) {
          ctx.strokeStyle = `rgba(201,168,76,${0.18 * (1 - md / 200)})`;
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.stroke();
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);
  return <canvas ref={ref} className="exec-network-canvas" aria-hidden="true" />;
};

/* ============================================================
   Left navigation config
   ============================================================ */
const navItems = [
  { key: "home",     label: "Home",                  Icon: Home },
  { key: "exec",     label: "Executive Intelligence", Icon: Brain },
  { key: "review",   label: "Founder Review",         Icon: Users },
  { key: "topology", label: "Neural Topology",        Icon: Network },
  { key: "graph",    label: "Knowledge Graph",        Icon: GitBranch },
  { key: "learning", label: "Learning Engine",        Icon: Sparkles },
  { key: "history",  label: "Decision History",       Icon: History },
  { key: "products", label: "Products",               Icon: Boxes },
  { key: "settings", label: "Settings",               Icon: Settings },
];

/* ============================================================
   Panel: Home
   ============================================================ */
const HomePanel = () => (
  <div className="panel">
    <div className="panel-header">
      <div>
        <div className="panel-eyebrow">Command Center</div>
        <h2 className="panel-title">Ecosystem Overview</h2>
      </div>
      <div className="panel-status">
        <span className="status-dot" /> All systems nominal
      </div>
    </div>

    <div className="kpi-grid">
      {[
        { label: "Decisions reviewed (30d)", value: "1,428", delta: "+18%", accent: "teal" },
        { label: "Founder-review acceptance", value: "94.6%", delta: "+2.1%", accent: "teal" },
        { label: "Cross-platform events", value: "62,340", delta: "+31%", accent: "gold" },
        { label: "Practice Health Score (avg)", value: "82", delta: "+4", accent: "teal" },
      ].map((k) => (
        <div key={k.label} className={`kpi kpi-${k.accent}`}>
          <div className="kpi-label">{k.label}</div>
          <div className="kpi-value">{k.value}</div>
          <div className="kpi-delta">{k.delta} <TrendingUp size={12} /></div>
        </div>
      ))}
    </div>

    <div className="split-2">
      <div className="glass-card">
        <div className="card-title-row">
          <h3>Signal feed</h3>
          <span className="card-sub">Last 24 hours</span>
        </div>
        <ul className="signal-list">
          {[
            { t: "14:22", src: "ProsthoAI", label: "Treatment plan finalized · TP-8121", state: "ok" },
            { t: "13:58", src: "ReimbursePro", label: "Claim auto-drafted · CL-44219", state: "ok" },
            { t: "13:11", src: "ZidaMD", label: "Clinical note scored 88/100", state: "ok" },
            { t: "12:04", src: "ReimbursePro", label: "Denial pattern detected · D2740 · Aetna", state: "warn" },
            { t: "10:47", src: "Bridge", label: "Prior auth pre-check · 3 flagged", state: "warn" },
          ].map((s, i) => (
            <li key={i} className={`sig sig-${s.state}`}>
              <span className="sig-t">{s.t}</span>
              <span className="sig-src">{s.src}</span>
              <span className="sig-text">{s.label}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="glass-card">
        <div className="card-title-row">
          <h3>Practice health · trend</h3>
          <span className="card-sub">14 days</span>
        </div>
        <div className="spark">
          {Array.from({ length: 14 }).map((_, i) => {
            const h = 30 + (Math.sin(i * 0.7) + 1) * 25 + Math.random() * 12;
            return (
              <span key={i} className="spark-bar" style={{ height: `${h}%`, animationDelay: `${i * 0.06}s` }} />
            );
          })}
        </div>
        <div className="spark-legend">
          <span>68</span>
          <span className="grad-text">82 today · +14</span>
        </div>
      </div>
    </div>
  </div>
);

/* ============================================================
   Panel: Executive Intelligence — decision queue
   ============================================================ */
const ExecPanel = () => (
  <div className="panel">
    <div className="panel-header">
      <div>
        <div className="panel-eyebrow">Executive Intelligence</div>
        <h2 className="panel-title">Decisions awaiting judgment</h2>
      </div>
      <div className="panel-status"><span className="status-dot" /> 6 pending</div>
    </div>

    <div className="decision-list">
      {[
        {
          id: "DEC-2041",
          title: "Approve automatic prior-auth submissions for D6010 (Aetna PPO)",
          risk: "medium",
          confidence: 82,
          reviewers: ["Clinical", "Billing", "Compliance"],
        },
        {
          id: "DEC-2040",
          title: "Route ARIA British-English narratives to ZidaMD by default",
          risk: "low",
          confidence: 91,
          reviewers: ["Clinical", "Product"],
        },
        {
          id: "DEC-2039",
          title: "Adopt Practice Health Score in ReimbursePro dashboard header",
          risk: "low",
          confidence: 96,
          reviewers: ["Product", "Design", "Founder"],
        },
        {
          id: "DEC-2038",
          title: "Introduce synthetic-only mode as default for beta tenants",
          risk: "high",
          confidence: 74,
          reviewers: ["Compliance", "Security", "Founder"],
        },
      ].map((d) => (
        <div key={d.id} className={`decision decision-${d.risk}`}>
          <div className="decision-head">
            <span className="decision-id">{d.id}</span>
            <span className={`chip risk-${d.risk}`}>{d.risk.toUpperCase()} RISK</span>
          </div>
          <div className="decision-title">{d.title}</div>
          <div className="decision-meta">
            <div className="reviewers">
              {d.reviewers.map((r) => (
                <span key={r} className="rev-chip">{r}</span>
              ))}
            </div>
            <div className="conf">
              <div className="conf-bar"><div className="conf-fill" style={{ width: `${d.confidence}%` }} /></div>
              <span>{d.confidence}% confidence</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ============================================================
   Panel: Founder Review — multi-expert perspectives
   ============================================================ */
const ReviewPanel = () => (
  <div className="panel">
    <div className="panel-header">
      <div>
        <div className="panel-eyebrow">Founder Review System</div>
        <h2 className="panel-title">Multi-expert review · DEC-2041</h2>
      </div>
      <div className="panel-status"><span className="status-dot" /> 3 of 4 reviewed</div>
    </div>

    <div className="review-decision-card">
      <div className="rdc-eyebrow">Decision</div>
      <div className="rdc-title">Approve automatic prior-auth submissions for D6010 (Aetna PPO)</div>
      <div className="rdc-sub">Auto-submit pre-authorized claims when confidence &gt; 80%. Currently manual review required.</div>
    </div>

    <div className="reviewer-grid">
      {[
        {
          role: "Clinical Perspective",
          reviewer: "ARIA · Prosthodontic reasoning",
          verdict: "approve-with-conditions",
          summary:
            "Clinically sound for straightforward D6010 cases with radiographic evidence. Recommend excluding immediate implants and bone-graft cases from auto-submission.",
        },
        {
          role: "Billing Perspective",
          reviewer: "ARIA · Revenue cycle",
          verdict: "approve",
          summary:
            "Aetna PPO D6010 approval rate is 91% over last 180 days for this practice. Auto-submission is projected to reduce claim-to-payment cycle by 4.2 days.",
        },
        {
          role: "Compliance Perspective",
          reviewer: "ARIA · HIPAA + payer policy",
          verdict: "flag",
          summary:
            "Requires a documented practice policy on override authority. Must retain human sign-off audit trail for 7 years. Beta must remain synthetic until BAA is executed.",
        },
        {
          role: "Founder Perspective",
          reviewer: "Dr. Ronik Seecharan, DMD",
          verdict: "pending",
          summary:
            "Awaiting founder judgment. All three expert reviews are visible above with source reasoning traces.",
        },
      ].map((r) => (
        <div key={r.role} className={`reviewer reviewer-${r.verdict}`}>
          <div className="reviewer-head">
            <span className="reviewer-role">{r.role}</span>
            <span className={`verdict verdict-${r.verdict}`}>{r.verdict.replace("-", " ")}</span>
          </div>
          <div className="reviewer-name">{r.reviewer}</div>
          <p className="reviewer-summary">{r.summary}</p>
        </div>
      ))}
    </div>

    <div className="review-actions">
      <button className="btn-primary" disabled>Approve as final</button>
      <button className="btn-glass" disabled>Request additional review</button>
      <button className="btn-glass" disabled>Reject</button>
    </div>
  </div>
);

/* ============================================================
   Panel: Neural Topology
   ============================================================ */
const TopologyPanel = () => (
  <div className="panel">
    <div className="panel-header">
      <div>
        <div className="panel-eyebrow">Neural Topology</div>
        <h2 className="panel-title">ARIA reasoning fabric</h2>
      </div>
      <div className="panel-status"><span className="status-dot" /> Model: Claude Sonnet 4.5</div>
    </div>

    <div className="topology-view">
      <svg viewBox="0 0 720 420" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="topoGrad" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#00E5CC" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#0066FF" stopOpacity="0.05" />
          </radialGradient>
        </defs>
        {/* Central ARIA node */}
        <circle cx="360" cy="210" r="60" fill="url(#topoGrad)" />
        <circle cx="360" cy="210" r="60" fill="none" stroke="#00E5CC" strokeOpacity="0.6" strokeWidth="1.5" />
        <text x="360" y="205" textAnchor="middle" className="topo-label">ARIA</text>
        <text x="360" y="222" textAnchor="middle" className="topo-sub">reasoning core</text>

        {/* Peripheral nodes */}
        {[
          { x: 100, y: 100, label: "Clinical",     color: "#00E5CC" },
          { x: 620, y: 100, label: "Billing",      color: "#C9A84C" },
          { x: 100, y: 320, label: "Compliance",   color: "#4d8eff" },
          { x: 620, y: 320, label: "Communication",color: "#4d8eff" },
          { x: 360, y: 40,  label: "Founder",      color: "#C9A84C" },
          { x: 360, y: 380, label: "Ecosystem",    color: "#00E5CC" },
        ].map((n, i) => (
          <g key={n.label}>
            <line x1="360" y1="210" x2={n.x} y2={n.y}
                  stroke={n.color} strokeOpacity="0.35" strokeWidth="1.2"
                  strokeDasharray="4 8" className={`topo-flow topo-flow-${i}`} />
            <circle cx={n.x} cy={n.y} r="28" fill="rgba(255,255,255,0.04)"
                    stroke={n.color} strokeOpacity="0.55" strokeWidth="1.5" />
            <text x={n.x} y={n.y + 4} textAnchor="middle" className="topo-node-label" fill={n.color}>
              {n.label}
            </text>
          </g>
        ))}
      </svg>
    </div>

    <div className="topology-legend">
      <div><span className="legend-dot" style={{ background: "#00E5CC" }} /> Clinical reasoning</div>
      <div><span className="legend-dot" style={{ background: "#C9A84C" }} /> Revenue reasoning</div>
      <div><span className="legend-dot" style={{ background: "#4d8eff" }} /> Governance</div>
    </div>
  </div>
);

/* ============================================================
   Panel: Knowledge Graph
   ============================================================ */
const GraphPanel = () => (
  <div className="panel">
    <div className="panel-header">
      <div>
        <div className="panel-eyebrow">Knowledge Graph</div>
        <h2 className="panel-title">Concept relationships</h2>
      </div>
      <div className="panel-status"><span className="status-dot" /> 12,481 nodes · 41,208 edges</div>
    </div>

    <div className="graph-nodes">
      {[
        { c: "Procedure", label: "D2740", meta: "Crown porcelain/ceramic", n: 34, color: "#00E5CC" },
        { c: "Diagnosis", label: "K02.9", meta: "Dental caries, unspecified", n: 22, color: "#4d8eff" },
        { c: "Payer",     label: "Aetna PPO", meta: "Commercial · Group", n: 18, color: "#C9A84C" },
        { c: "Rule",      label: "Prior Auth", meta: "Threshold: age 21+", n: 9,  color: "#4d8eff" },
        { c: "Evidence",  label: "Periapical", meta: "Radiographic support", n: 12, color: "#00E5CC" },
        { c: "Outcome",   label: "Approved",   meta: "91% first-pass",       n: 27, color: "#00E5CC" },
      ].map((g) => (
        <div key={g.label} className="graph-node">
          <div className="graph-node-class" style={{ color: g.color, borderColor: `${g.color}55` }}>{g.c}</div>
          <div className="graph-node-label">{g.label}</div>
          <div className="graph-node-meta">{g.meta}</div>
          <div className="graph-node-count">{g.n} connections</div>
        </div>
      ))}
    </div>
  </div>
);

/* ============================================================
   Panel: Learning Engine
   ============================================================ */
const LearningPanel = () => (
  <div className="panel">
    <div className="panel-header">
      <div>
        <div className="panel-eyebrow">Learning Engine</div>
        <h2 className="panel-title">ARIA continuous evaluation</h2>
      </div>
      <div className="panel-status"><span className="status-dot" /> Eval window: 7 days</div>
    </div>

    <div className="learning-grid">
      {[
        { label: "Clinical accuracy",   v: 94, color: "#00E5CC" },
        { label: "Coding precision",    v: 91, color: "#C9A84C" },
        { label: "Documentation score", v: 87, color: "#00E5CC" },
        { label: "Communication tone",  v: 96, color: "#4d8eff" },
        { label: "Founder acceptance",  v: 95, color: "#C9A84C" },
        { label: "Hallucination rate",  v: 2,  color: "#ff8a4d", invert: true },
      ].map((m) => (
        <div key={m.label} className="learning-metric">
          <div className="lm-label">{m.label}</div>
          <div className="lm-ring">
            <svg viewBox="0 0 60 60">
              <circle cx="30" cy="30" r="26" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
              <circle
                cx="30" cy="30" r="26"
                fill="none" stroke={m.color} strokeWidth="4" strokeLinecap="round"
                strokeDasharray={`${(m.v / 100) * 163} 163`}
                transform="rotate(-90 30 30)"
              />
            </svg>
            <span className="lm-val" style={{ color: m.color }}>{m.v}{m.invert ? "%" : ""}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ============================================================
   Panel: Decision History
   ============================================================ */
const HistoryPanel = () => (
  <div className="panel">
    <div className="panel-header">
      <div>
        <div className="panel-eyebrow">Decision History</div>
        <h2 className="panel-title">Audit-grade trail</h2>
      </div>
      <div className="panel-status"><span className="status-dot" /> Immutable · 7yr retention</div>
    </div>

    <div className="history-table">
      <div className="ht-head">
        <span>ID</span><span>Decision</span><span>Reviewers</span><span>Outcome</span><span>Date</span>
      </div>
      {[
        { id: "DEC-2037", t: "Enable Practice Health Score in ReimbursePro",     rev: "P/D/F", o: "approved",  d: "2026-07-06" },
        { id: "DEC-2036", t: "Bridge synthetic-only default for beta",           rev: "C/S/F", o: "approved",  d: "2026-07-04" },
        { id: "DEC-2035", t: "Retire legacy manual claim path in ReimbursePro",  rev: "B/F",   o: "rejected",  d: "2026-07-01" },
        { id: "DEC-2034", t: "Add ZidaMD ICD-10 2026 refresh cycle",             rev: "C/P/F", o: "approved",  d: "2026-06-28" },
        { id: "DEC-2033", t: "Introduce ARIA British-English narrative default", rev: "C/P/F", o: "deferred",  d: "2026-06-24" },
      ].map((r) => (
        <div key={r.id} className="ht-row">
          <span className="ht-id">{r.id}</span>
          <span className="ht-t">{r.t}</span>
          <span className="ht-rev">{r.rev}</span>
          <span className={`ht-o ht-o-${r.o}`}>{r.o}</span>
          <span className="ht-d">{r.d}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ============================================================
   Panel: Products
   ============================================================ */
const ProductsPanel = () => (
  <div className="panel">
    <div className="panel-header">
      <div>
        <div className="panel-eyebrow">Products</div>
        <h2 className="panel-title">The CamelionAI ecosystem</h2>
      </div>
    </div>

    <div className="products-grid">
      {[
        { name: "ProsthoAI",    tag: "Dental Specialty",  Icon: Stethoscope,  href: "https://prosthoai.com",              color: "#00E5CC" },
        { name: "ReimbursePro", tag: "Revenue Recovery",  Icon: Wallet,       href: "https://reimburse.pro",              color: "#C9A84C" },
        { name: "ZidaMD",       tag: "Physician OS",      Icon: HeartPulse,   href: "https://zidamd.com",                 color: "#4d8eff" },
        { name: "Future Platforms", tag: "In development", Icon: Sparkles,    href: null,                                 color: "#8892A4" },
      ].map((p) => (
        <a key={p.name}
           href={p.href || undefined}
           target={p.href ? "_blank" : undefined}
           rel="noopener noreferrer"
           className={`prod-mini ${!p.href ? "prod-disabled" : ""}`}
           style={{ borderColor: `${p.color}30` }}>
          <span className="prod-mini-icon" style={{ color: p.color, background: `${p.color}12`, borderColor: `${p.color}55` }}>
            <p.Icon size={18} />
          </span>
          <div>
            <div className="prod-mini-name">{p.name}</div>
            <div className="prod-mini-tag">{p.tag}</div>
          </div>
          {p.href && <ArrowRight size={14} className="prod-mini-arrow" />}
        </a>
      ))}
    </div>
  </div>
);

/* ============================================================
   Panel: Settings
   ============================================================ */
const SettingsPanel = () => (
  <div className="panel">
    <div className="panel-header">
      <div>
        <div className="panel-eyebrow">Settings</div>
        <h2 className="panel-title">Executive Center configuration</h2>
      </div>
    </div>

    <div className="settings-list">
      {[
        { k: "PHI Mode",           v: "synthetic_only",                sub: "All Bridge and Executive data is de-identified during beta." },
        { k: "LLM routing",        v: "Claude Sonnet 4.5 (default)",   sub: "British-English narrative mode enabled for ZidaMD." },
        { k: "Audit retention",    v: "7 years",                       sub: "Immutable log · Practice-scoped access controls." },
        { k: "Founder review",     v: "Required for HIGH risk",        sub: "Manual sign-off required · Full reasoning trace preserved." },
      ].map((s) => (
        <div key={s.k} className="setting-row">
          <div>
            <div className="setting-k">{s.k}</div>
            <div className="setting-sub">{s.sub}</div>
          </div>
          <div className="setting-v">{s.v}</div>
        </div>
      ))}
    </div>
  </div>
);

/* ============================================================
   Panel router
   ============================================================ */
const renderPanel = (key) => {
  switch (key) {
    case "home":     return <HomePanel />;
    case "exec":     return <ExecPanel />;
    case "review":   return <ReviewPanel />;
    case "topology": return <TopologyPanel />;
    case "graph":    return <GraphPanel />;
    case "learning": return <LearningPanel />;
    case "history":  return <HistoryPanel />;
    case "products": return <ProductsPanel />;
    case "settings": return <SettingsPanel />;
    default:         return <HomePanel />;
  }
};

/* ============================================================
   Page
   ============================================================ */
const ExecutivePage = () => {
  const nav = useNavigate();
  const [active, setActive] = useState("home");
  const [launched, setLaunched] = useState(false);

  useEffect(() => {
    document.getElementById("emergent-badge")?.remove();
    window.scrollTo(0, 0);
  }, []);

  const launch = () => {
    setLaunched(true);
    setTimeout(() => {
      document.getElementById("eic-dashboard")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <div className="exec-page" data-testid="exec-page">
      <NetworkBg />

      <SiteNav />

      {/* Hero */}
      <section className="exec-hero">
        <div className="exec-eyebrow">
          <Compass size={14} /> Executive Intelligence Center
        </div>
        <div className="exec-compass-wrap">
          <img
            src="/brand/camelion-logo-full.png"
            alt="CamelionAI — chameleon over compass"
            className="exec-compass"
            data-testid="exec-compass"
          />
        </div>
        <div className="exec-tagline">Adaptive Healthcare Intelligence</div>
        <h1 className="exec-title" data-testid="exec-title">
          Executive Intelligence <span className="grad-text">for modern healthcare.</span>
        </h1>
        <p className="exec-sub">
          Transform data into knowledge.<br />
          Transform knowledge into judgment.<br />
          <span className="grad-text">Transform judgment into better healthcare decisions.</span>
        </p>
        <div className="exec-ctas">
          <button className="btn-primary" onClick={launch} data-testid="exec-launch-btn">
            Launch Executive Intelligence Center <ArrowRight size={14} />
          </button>
          <button className="btn-glass" onClick={() => nav("/#platforms")} data-testid="exec-review-btn">
            Review Existing Projects
          </button>
        </div>

        <div className="exec-pillars">
          {[
            { i: Shield,     l: "Precision" },
            { i: Activity,   l: "Trust" },
            { i: Brain,      l: "Intelligence" },
            { i: CheckCircle2, l: "Better Decisions" },
          ].map(({ i: I, l }) => (
            <div key={l} className="pillar"><I size={16} /> {l}</div>
          ))}
        </div>
      </section>

      {/* Dashboard */}
      {launched && (
        <section id="eic-dashboard" className="eic-dashboard" data-testid="eic-dashboard">
          <aside className="eic-side">
            <div className="eic-side-head">
              <Compass size={14} /> <span>Command</span>
            </div>
            <ul className="eic-nav">
              {navItems.map(({ key, label, Icon }) => (
                <li key={key}>
                  <button
                    className={`eic-nav-btn ${active === key ? "active" : ""}`}
                    onClick={() => setActive(key)}
                    data-testid={`eic-nav-${key}`}
                  >
                    <Icon size={16} />
                    <span>{label}</span>
                    {active === key && <span className="eic-nav-glow" />}
                  </button>
                </li>
              ))}
            </ul>
            <div className="eic-side-foot">
              <div className="eic-user">
                <div className="eic-avatar">RS</div>
                <div>
                  <div className="eic-user-name">Dr. Ronik Seecharan</div>
                  <div className="eic-user-role">Founder · CamelionAI</div>
                </div>
              </div>
            </div>
          </aside>

          <main className="eic-main">
            {renderPanel(active)}
          </main>
        </section>
      )}
    </div>
  );
};

export default ExecutivePage;
